import {
  faArrowDownToSquare,
  faArrowUpFromSquare,
  faFileCircleMinus,
  faFileCirclePlus,
  faFilePen,
  faFiles,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup, Toolbar } from "@progress/kendo-react-buttons";
import { getter } from "@progress/kendo-react-common";
import { Window } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  columnsConstructorSessionData,
  editedPropertyValues as editedColumnPropertyValues,
  selectedColumnFieldPropertyValues,
  selectedColumnIndex,
} from "../../Fields/ColumnsConstructor/ColumnsConstructorSignals";
import { isGalleryEditor, selectedFieldPropertyValues } from "../../Template/NewTemplateSignals";
import {
  editedPropertyValues,
  selectedFieldName,
  selectedGalleryFieldPropertyValues,
} from "../GalleryConstructor/GalleryConstructorSignals";
import { conditionalFormattingVisibility } from "../Store/CommonSignals";
import "./ConditionRuleBuilder.css";
import ConditionRuleEditor from "./ConditionRuleEditor";

const cellWithBackGround = (props) => {
  return <td style={props.dataItem.style}>AaBbCcYyZz</td>;
};

const cellWithCheckbox = (props) => {
  return (
    <td readOnly={true}>
      <Checkbox
        value={props.dataItem[props.field]}
        readOnly={true}
        disabled={true}
      />
    </td>
  );
};

const ConditionRuleBuilder = ({ handlePropertyChange, fieldType }) => {
  const [select] = useState({});
  const getCurrentPropertyValues = () => {
    if (isGalleryEditor.value) {
      return {
        ...selectedGalleryFieldPropertyValues.value,
        ...editedPropertyValues.value?.[selectedFieldName.value],
      };
    } else if (columnsConstructorSessionData.value.length > 0) {
      return {
        ...selectedColumnFieldPropertyValues.value,
        ...editedColumnPropertyValues.value?.[selectedColumnIndex.value],
      };
    } else {
      return selectedFieldPropertyValues.value;
    }
  };
  const currentPropertyValues = getCurrentPropertyValues();
  const internalRules = currentPropertyValues?.ConditionalFormatting || [];
  const [maxId, setMaxId] = useState(0);
  const gridRef = useRef(null);
  const SELECTED_FIELD = "selected";
  const idGetter = getter("id");
  const [selectedState, setSelectedState] = useState({});
  const [toggleEditRule, setToggleEditRule] = useState(false);

  const [dataState, setDataState] = useState(
    internalRules?.map((dataItem) => ({
      selected: false,
      ...dataItem,
    })) || []
  );

  const cellWithEnabledCheckbox = (props) => {
    return (
      <td>
        <Checkbox
          value={props.dataItem[props.field]}
          onChange={(e) => {
            const newSelectedState = {};
            dataState.forEach((item) => {
              newSelectedState[item.id] = false;
            });
            newSelectedState[props.dataItem.id] = e.value;
            setSelectedState(newSelectedState);
          }}
        />
      </td>
    );
  };

  useEffect(() => {
    const newMaxId = Math.max(...(dataState.length > 0 ? dataState.map((item) => item.id) : [0]));
    setMaxId(newMaxId);
  }, [dataState]);

  const handleSubmit = () => {
    const cleanedDataState = dataState.map(({ selected, ...item }) => item);
    handlePropertyChange({ propName: "ConditionalFormatting", propValue: cleanedDataState });
    conditionalFormattingVisibility.value = false;
  };

  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: "id",
      });
      setSelectedState(newSelectedState);
    },
    [selectedState]
  );

  const handleNewRule = () => {
    const newRules = [...dataState];
    newRules.push({
      id: maxId + 1,
      rule: "",
      style: { backgroundColor: "white" },
      stopProcessing: false,
      selected: false,
    });
    setMaxId(maxId + 1);
    setDataState([...newRules]);
  };

  const handleEditRule = () => {
    if (
      dataState
        .map((item) => ({
          ...item,
          [SELECTED_FIELD]: selectedState[idGetter(item)],
        }))
        .filter((item) => item[SELECTED_FIELD])[0]
    ) {
      setToggleEditRule(!toggleEditRule);
    }
  };

  const handleDuplicateRule = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];
    selectedRule.id = maxId + 1;
    selectedRule.selected = false;
    setDataState([...newRules, selectedRule]);
  };

  const handleDeleteRule = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];
    const index = newRules.findIndex((item) => item.id === selectedRule.id);
    newRules.splice(index, 1);
    setDataState([...newRules]);
  };

  const handleMoveUp = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];

    const index = newRules.findIndex((item) => item.id === selectedRule.id);

    if (index > 0) {
      // Swap positions
      const tempRule = newRules[index - 1];
      newRules[index - 1] = newRules[index];
      newRules[index] = tempRule;

      // Swap id values
      const tempId = newRules[index - 1].id;
      newRules[index - 1].id = newRules[index].id;
      newRules[index].id = tempId;

      // Swap selected values
      newRules[index - 1][SELECTED_FIELD] = true;
      newRules[index][SELECTED_FIELD] = false;

      // Update the selectedState
      const newSelectedState = { ...selectedState };
      newSelectedState[tempId] = false; // The item that's now below
      newSelectedState[newRules[index - 1].id] = true; // The item that's now on the position of the selected rule
      setSelectedState(newSelectedState);
    }

    setDataState([...newRules]);
  };

  const handleMoveDown = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];

    const index = newRules.findIndex((item) => item.id === selectedRule.id);

    // Check if the item is not the last one
    if (index < newRules.length - 1) {
      // Swap positions
      const tempRule = newRules[index + 1];
      newRules[index + 1] = newRules[index];
      newRules[index] = tempRule;

      // Swap id values
      const tempId = newRules[index + 1].id;
      newRules[index + 1].id = newRules[index].id;
      newRules[index].id = tempId;

      // Swap selected values
      newRules[index + 1][SELECTED_FIELD] = true;
      newRules[index][SELECTED_FIELD] = false;

      // Update the selectedState
      const newSelectedState = { ...selectedState };
      newSelectedState[tempId] = false; // The item that's now above
      newSelectedState[newRules[index + 1].id] = true; // The item that's now on the position of the selected rule
      setSelectedState(newSelectedState);
    }

    setDataState([...newRules]);
  };

  return (
    <div className="condition-rule-page-content">
      <div className="condition-rule-container">
        <Toolbar>
          <ButtonGroup>
            <Button onClick={handleNewRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFileCirclePlus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                New Rule
              </div>
            </Button>
            <Button onClick={handleEditRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFilePen}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Edit Rule
              </div>
            </Button>
            <Button onClick={handleDeleteRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  className="toolbar-button-icon"
                  icon={faFileCircleMinus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Delete Rule
              </div>
            </Button>
            <Button onClick={handleDuplicateRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFiles}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Duplicate Rule
              </div>
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button onClick={handleMoveUp}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowUpFromSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
            <Button onClick={handleMoveDown}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowDownToSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
          </ButtonGroup>
        </Toolbar>
        <Grid
          className="rules-grid"
          data={dataState.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState[idGetter(item)],
          }))}
          key="selected"
          dataItemKey="id"
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "single",
          }}
          onSelectionChange={onSelectionChange}
          ref={gridRef}
          select={select}
        >
          <GridColumn
            field={SELECTED_FIELD}
            width="50px"
            title=" "
            cells={{ data: cellWithEnabledCheckbox }}
          />
          <GridColumn
            field="rule"
            title="Rule (applied in order shown)"
          />
          <GridColumn
            field="style"
            title="Format"
            cells={{
              data: cellWithBackGround,
            }}
          />
          <GridColumn
            field="stopProcessing"
            title="Stop if True"
            cells={{
              data: cellWithCheckbox,
            }}
          />
        </Grid>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          onClick={() => handleSubmit()}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={() => (conditionalFormattingVisibility.value = false)}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Cancel
        </Button>
      </div>
      {toggleEditRule && (
        <Window
          onClose={handleEditRule}
          className="condition-builder-window"
          initialHeight={850}
          initialWidth={1200}
          title={"Edit Rule"}
        >
          <ConditionRuleEditor
            rule={
              dataState
                ?.map((item) => ({
                  ...item,
                  [SELECTED_FIELD]: selectedState[idGetter(item)],
                }))
                ?.filter((item) => item[SELECTED_FIELD])[0]
            }
            onClose={() => {
              setToggleEditRule(!toggleEditRule);
            }}
            fieldType={fieldType}
            dataState={dataState}
            setDataState={setDataState}
          />
        </Window>
      )}
    </div>
  );
};

export default ConditionRuleBuilder;
